/**
 * EmptyState - Shared Empty State Component for All ML Infrastructure Pages
 * 
 * Provides consistent empty state display with optional action.
 * 
 * Following BRAIN-PRE-CODE-CHECKLIST.md:
 * - Clean single imports
 * - User-friendly messaging
 */

import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { Inbox as InboxIcon } from '@mui/icons-material';

interface EmptyStateProps {
  title?: string;
  message?: string;
  actionLabel?: string;
  onAction?: () => void;
  icon?: React.ReactNode;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  title = 'No Data Available',
  message = 'There is no data to display at this time.',
  actionLabel,
  onAction,
  icon
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 300,
        py: 6,
        px: 3,
        textAlign: 'center'
      }}
    >
      <Box sx={{ mb: 2, color: 'text.secondary', opacity: 0.5 }}>
        {icon || <InboxIcon sx={{ fontSize: 64 }} />}
      </Box>
      <Typography variant="h6" color="text.primary" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400 }}>
        {message}
      </Typography>
      {actionLabel && onAction && (
        <Button variant="contained" onClick={onAction}>
          {actionLabel}
        </Button>
      )}
    </Box>
  );
};

export default EmptyState;
